import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Avatar,
  Tooltip,
  FormControl,
  Select,
  InputLabel,
} from "@mui/material";
import {
  Add,
  Search,
  MoreVert,
  Edit,
  ContentCopy,
  Delete,
  Refresh,
  Visibility,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const StyledTableRow = styled(TableRow)`
  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
`;

const ProductImage = styled(Avatar)`
  width: 56px;
  height: 56px;
  border-radius: 8px;
  background-color: #f5f5f5;
`;

type ProductSummary = {
  id: number;
  title: string;
  image: string;
  fireRating: string;
  category: string;
  slug: string;
  model3d?: { url: string; type: "obj" | "stl" | "fbx" };
  price?: number;
  showPrice?: boolean;
};

const ProductsAdmin: React.FC = () => {
  const navigate = useNavigate();

  // Data state
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [categories, setCategories] = useState<{ value: string; key?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterFireRating, setFilterFireRating] = useState("");

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuProduct, setMenuProduct] = useState<ProductSummary | null>(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<ProductSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        fetch("/data/products/index.json", { cache: "no-store" }),
        fetch("/data/categories/index.json", { cache: "no-store" }),
      ]);

      const productsData = await productsRes.json();
      const categoriesData = await categoriesRes.json();

      setProducts(Array.isArray(productsData.items) ? productsData.items : []);
      setCategories(Array.isArray(categoriesData.items) ? categoriesData.items : []);
    } catch (e: any) {
      setError("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  // Filtered and searched products
  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(query) ||
          p.slug.toLowerCase().includes(query) ||
          p.category.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (filterCategory) {
      result = result.filter((p) => p.category === filterCategory);
    }

    // Fire rating filter
    if (filterFireRating) {
      result = result.filter((p) => p.fireRating === filterFireRating);
    }

    return result;
  }, [products, searchQuery, filterCategory, filterFireRating]);

  // Pagination
  const paginatedProducts = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredProducts.slice(start, start + rowsPerPage);
  }, [filteredProducts, page, rowsPerPage]);

  // Get unique fire ratings
  const fireRatings = useMemo(() => {
    const ratings = new Set(products.map((p) => p.fireRating).filter(Boolean));
    return Array.from(ratings);
  }, [products]);

  // Selection handlers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(filteredProducts.map((p) => p.slug));
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (slug: string) => {
    setSelected((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, product: ProductSummary) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuProduct(product);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuProduct(null);
  };

  // Actions
  const handleEdit = (slug: string) => {
    navigate(`/admin/products/${slug}/edit`);
    handleMenuClose();
  };

  const handleClone = async (product: ProductSummary) => {
    try {
      const res = await fetch(`/data/products/${product.slug}/index.json`, { cache: "no-store" });
      const data = await res.json();
      // Store cloned data and navigate to new product page
      localStorage.setItem("productClone", JSON.stringify({
        ...data,
        slug: "",
        locales: {
          ...data.locales,
          tr: { ...data.locales?.tr, title: `${data.locales?.tr?.title || ""} (Kopya)` },
          en: { ...data.locales?.en, title: `${data.locales?.en?.title || ""} (Copy)` },
        },
      }));
      navigate("/admin/products/new?clone=true");
    } catch (e) {
      setError("Kopyalanamadı");
    }
    handleMenuClose();
  };

  const handleDeleteClick = (product: ProductSummary) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    try {
      setDeleting(true);
      // Delete product folder
      await fetch("/api/content/remove", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-admin-key": localStorage.getItem("adminKey") || "",
        },
        body: JSON.stringify({
          path: `data/products/${productToDelete.slug}`,
          message: `chore(products): remove ${productToDelete.slug}`,
        }),
      });

      // Update index
      const filtered = products.filter((p) => p.slug !== productToDelete.slug);
      await fetch("/api/content/github", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-admin-key": localStorage.getItem("adminKey") || "",
        },
        body: JSON.stringify({
          path: "data/products/index.json",
          contentBase64: btoa(unescape(encodeURIComponent(JSON.stringify({ items: filtered }, null, 2)))),
          message: `chore(products): update index remove ${productToDelete.slug}`,
        }),
      });

      setProducts(filtered);
      setSuccess("Ürün silindi");
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    } catch (e) {
      setError("Silinemedi");
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`${selected.length} ürün silinecek. Emin misiniz?`)) return;

    try {
      setLoading(true);
      for (const slug of selected) {
        await fetch("/api/content/remove", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-admin-key": localStorage.getItem("adminKey") || "",
          },
          body: JSON.stringify({
            path: `data/products/${slug}`,
            message: `chore(products): bulk remove ${slug}`,
          }),
        });
      }

      const filtered = products.filter((p) => !selected.includes(p.slug));
      await fetch("/api/content/github", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-admin-key": localStorage.getItem("adminKey") || "",
        },
        body: JSON.stringify({
          path: "data/products/index.json",
          contentBase64: btoa(unescape(encodeURIComponent(JSON.stringify({ items: filtered }, null, 2)))),
          message: `chore(products): bulk remove ${selected.length} items`,
        }),
      });

      setProducts(filtered);
      setSelected([]);
      setSuccess(`${selected.length} ürün silindi`);
    } catch (e) {
      setError("Toplu silme başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#1a1a1a" }}>
            Ürünler
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Toplam {products.length} ürün
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/admin/products/new")}
          sx={{ minWidth: 140 }}
        >
          Yeni Ürün
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, p: 2, borderRadius: 3, border: "1px solid #eee", boxShadow: "none" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          sx={{ flexWrap: { md: "wrap" } }}
        >
          <TextField
            placeholder="Ürün ara..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: "100%", md: 250 } }}
          />

          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 150 } }}>
            <InputLabel>Kategori</InputLabel>
            <Select
              value={filterCategory}
              label="Kategori"
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <MenuItem value="">Tümü</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.key || cat.value} value={cat.key || cat.value}>
                  {cat.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 150 } }}>
            <InputLabel>Yangın Sınıfı</InputLabel>
            <Select
              value={filterFireRating}
              label="Yangın Sınıfı"
              onChange={(e) => setFilterFireRating(e.target.value)}
            >
              <MenuItem value="">Tümü</MenuItem>
              {fireRatings.map((rating) => (
                <MenuItem key={rating} value={rating}>
                  {rating}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flex: 1, display: { xs: "none", md: "block" } }} />

          <Stack direction="row" spacing={1}>
            {selected.length > 0 && (
              <Button
                color="error"
                variant="outlined"
                startIcon={<Delete />}
                onClick={handleBulkDelete}
                size="small"
              >
                {selected.length} Sil
              </Button>
            )}
            <Tooltip title="Yenile">
              <IconButton onClick={loadData} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 3, border: "1px solid #eee", boxShadow: "none", overflow: "hidden" }}>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafafa" }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < filteredProducts.length}
                    checked={filteredProducts.length > 0 && selected.length === filteredProducts.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Ürün</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Kategori</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Yangın Sınıfı</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Özellikler</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Skeleton loading
                Array.from({ length: 5 }).map((_, idx) => (
                  <TableRow key={idx}>
                    <TableCell padding="checkbox">
                      <Skeleton variant="rectangular" width={20} height={20} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Skeleton variant="rounded" width={56} height={56} />
                        <Box>
                          <Skeleton width={150} />
                          <Skeleton width={100} />
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell><Skeleton width={80} /></TableCell>
                    <TableCell><Skeleton width={60} /></TableCell>
                    <TableCell><Skeleton width={100} /></TableCell>
                    <TableCell><Skeleton width={40} /></TableCell>
                  </TableRow>
                ))
              ) : paginatedProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">
                      {searchQuery || filterCategory || filterFireRating
                        ? "Arama kriterlerine uygun ürün bulunamadı"
                        : "Henüz ürün eklenmemiş"}
                    </Typography>
                    {!searchQuery && !filterCategory && !filterFireRating && (
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate("/admin/products/new")}
                        sx={{ mt: 2 }}
                      >
                        İlk Ürünü Ekle
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedProducts.map((product) => (
                  <StyledTableRow
                    key={product.slug}
                    selected={selected.includes(product.slug)}
                    onClick={() => handleEdit(product.slug)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.includes(product.slug)}
                        onChange={() => handleSelectOne(product.slug)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <ProductImage
                          variant="rounded"
                          src={product.image}
                          alt={product.title}
                        >
                          {!product.image && product.title.charAt(0)}
                        </ProductImage>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                            {product.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            /{product.slug}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={categories.find((c) => (c.key || c.value) === product.category)?.value || product.category || "-"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {product.fireRating ? (
                        <Chip label={product.fireRating} size="small" color="error" />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {product.model3d && (
                          <Chip label="3D" size="small" color="success" />
                        )}
                        {product.showPrice && (
                          <Chip label="Fiyatlı" size="small" color="info" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, product)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredProducts.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Sayfa başına:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => menuProduct && handleEdit(menuProduct.slug)}>
          <Edit sx={{ mr: 1.5, fontSize: 20 }} />
          Düzenle
        </MenuItem>
        <MenuItem onClick={() => window.open(`/products/${menuProduct?.slug}`, "_blank")}>
          <Visibility sx={{ mr: 1.5, fontSize: 20 }} />
          Görüntüle
        </MenuItem>
        <MenuItem onClick={() => menuProduct && handleClone(menuProduct)}>
          <ContentCopy sx={{ mr: 1.5, fontSize: 20 }} />
          Kopyala
        </MenuItem>
        <MenuItem onClick={() => menuProduct && handleDeleteClick(menuProduct)} sx={{ color: "error.main" }}>
          <Delete sx={{ mr: 1.5, fontSize: 20 }} />
          Sil
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Ürünü Sil</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{productToDelete?.title}</strong> ürününü silmek istediğinize emin misiniz?
            Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            İptal
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "Siliniyor..." : "Sil"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess("")}>
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductsAdmin;
